import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Alert,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { findOption, optionValuesEqual, renderOptionWithHighlight, searchTextField } from "utils/searchableOptions";
import { TIMEZONES, getTimezoneByValue, type Timezone } from "../../../../../utils/timezones";

export interface TimeWindow {
  startTime: string; // HH:MM format
  endTime: string;   // HH:MM format
}

export interface TimeFilterConfig {
  timeWindows: TimeWindow[];
  daysOfWeek: number[]; // 0=Sunday, 1=Monday, etc.
  timezone: string;
  behavior: "match" | "bypass";
}

interface TimeFilterProps {
  value?: TimeFilterConfig;
  onChange: (config: TimeFilterConfig | undefined) => void;
  onValidationChange: (isValid: boolean) => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];

const DEFAULT_TIME_WINDOW: TimeWindow = {
  startTime: "09:00",
  endTime: "17:00",
};

const DEFAULT_CONFIG: TimeFilterConfig = {
  timeWindows: [DEFAULT_TIME_WINDOW],
  daysOfWeek: [1, 2, 3, 4, 5], // Monday-Friday
  timezone: "America/New_York",
  behavior: "match",
};

const behaviorOptions = [
  { value: "match", label: "Apply rule ONLY during time windows" },
  { value: "bypass", label: "Apply rule EXCEPT during time windows" },
];

export function TimeFilter(props: TimeFilterProps) {
  const { onChange } = props;
  const { onValidationChange } = props;
  const [enabled, setEnabled] = useState<boolean>(!!props.value);
  const [config, setConfig] = useState<TimeFilterConfig>(
    props.value || DEFAULT_CONFIG
  );
  const [error, setError] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");

  const validateTimeFormat = (time: string): boolean => {
    const regex = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
    return regex.test(time);
  };

  const validateConfig = useCallback((cfg: TimeFilterConfig): boolean => {
    // Must have timezone
    if (!cfg.timezone || cfg.timezone.trim() === "") {
      setError("Please select a timezone");
      return false;
    }

    if (cfg.daysOfWeek.length === 0) {
      setError("Please select at least one day of the week");
      return false;
    }

    // Must have at least one time window
    if (cfg.timeWindows.length === 0) {
      setError("At least one time window is required");
      return false;
    }

    // Validate the time window
    const window = cfg.timeWindows[0];
    if (!validateTimeFormat(window.startTime)) {
      setError("Invalid start time format");
      return false;
    }

    if (!validateTimeFormat(window.endTime)) {
      setError("Invalid end time format");
      return false;
    }

    if (window.startTime === window.endTime) {
      setError("Start time and end time cannot be the same");
      return false;
    }

    // Check if time crosses midnight and set warning message
    if (window.startTime > window.endTime) {
      setWarningMessage("Time window crosses midnight");
    } else {
      setWarningMessage("");
    }

    setError("");

    return true;
  }, []);

  useEffect(() => {
    if (enabled) {
      // Validate before sending
      const isValid = validateConfig(config);
      onValidationChange(isValid);
      if (isValid) {
        onChange(config);
      }
    } else {
      onValidationChange(true);
      onChange(undefined);
    }
  }, [enabled, config, onChange, validateConfig, onValidationChange]);

  const handleTimeWindowChange = (
    field: "startTime" | "endTime",
    value: string
  ) => {
    // Update the single time window
    const newWindow = { ...config.timeWindows[0], [field]: value };

    // Basic format validation
    if (!validateTimeFormat(value)) {
      setError("Invalid time format. Use HH:MM (24-hour format)");
      return;
    }

    // Check if start and end are the same
    if (newWindow.startTime === newWindow.endTime) {
      setError("Start time and end time cannot be the same");
      return;
    }

    // Check for midnight crossing and show info message
    if (newWindow.startTime > newWindow.endTime) {
      setWarningMessage("Time window crosses midnight (e.g., 22:00 to 06:00 next day)");
    } else {
      setWarningMessage("");
    }

    setConfig({
      ...config,
      timeWindows: [newWindow],
    });
    setError("");
  };

  const handleDayToggle = (day: number) => {
    const newDays = config.daysOfWeek.includes(day)
      ? config.daysOfWeek.filter((d) => d !== day)
      : [...config.daysOfWeek, day].sort();

    setConfig({
      ...config,
      daysOfWeek: newDays,
    });
  };

  const handleTimezoneChange = (timezone: Timezone | null) => {
    if (timezone) {
      setConfig({
        ...config,
        timezone: timezone.value,
      });
    }
  };

  const handleBehaviorChange = (behavior: "match" | "bypass") => {
    setConfig({
      ...config,
      behavior,
    });
  };

  const selectedTimezone = getTimezoneByValue(config.timezone);

  return (
    <Box sx={{ marginBottom: 3 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
        }
        label="Enable Time-Based Filter"
      />

      {enabled && (
        <Box sx={{ marginLeft: 4, marginTop: 2 }}>
          {error && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {error}
            </Alert>
          )}

          {/* Behavior Selection */}
          <FormControl fullWidth sx={{ marginBottom: 3 }}>
            <FormLabel>Filter Behavior</FormLabel>
            <Autocomplete
              fullWidth
              disableClearable={true as boolean}
              options={behaviorOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={optionValuesEqual}
              value={findOption(behaviorOptions, config.behavior || null)}
              onChange={(_, option) =>
                handleBehaviorChange((option?.value || "match") as "match" | "bypass")
              }
              renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(behaviorOptions, config.behavior || null)?.label)}
              renderInput={(params) => searchTextField(params)}
            />
            <Typography variant="caption" sx={{ marginTop: 1 }}>
              {config.behavior === "match"
                ? "The rule will only be evaluated during the specified time windows"
                : "The rule will be bypassed during the specified time windows"}
            </Typography>
          </FormControl>

          {/* Time Window */}
          <FormControl fullWidth sx={{ marginBottom: 3 }}>
            <FormLabel>Time Window</FormLabel>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ marginTop: 1 }}
            >
              <TextField
                label="Start Time"
                type="time"
                value={config.timeWindows[0].startTime}
                onChange={(e) =>
                  handleTimeWindowChange("startTime", e.target.value)
                }
                size="small"
                sx={{ width: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min intervals
                }}
              />
              <Typography>to</Typography>
              <TextField
                label="End Time"
                type="time"
                value={config.timeWindows[0].endTime}
                onChange={(e) =>
                  handleTimeWindowChange("endTime", e.target.value)
                }
                size="small"
                sx={{ width: 150 }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 min intervals
                }}
              />
            </Stack>
            {warningMessage && (
              <Alert severity="info" sx={{ marginTop: 1 }}>
                {warningMessage}
              </Alert>
            )}
            <Typography variant="caption" sx={{ marginTop: 1 }}>
              Use 24-hour format (e.g., 09:00, 17:30). Ranges can cross
              midnight (e.g., 22:00 to 06:00 next day).
            </Typography>
          </FormControl>

          {/* Days of Week */}
          <FormControl fullWidth sx={{ marginBottom: 3 }}>
            <FormLabel>Days of Week</FormLabel>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", marginTop: 1 }}>
              {DAYS_OF_WEEK.map((day) => (
                <Chip
                  key={day.value}
                  label={day.short}
                  onClick={() => handleDayToggle(day.value)}
                  color={
                    config.daysOfWeek.includes(day.value)
                      ? "primary"
                      : "default"
                  }
                  variant={
                    config.daysOfWeek.includes(day.value)
                      ? "filled"
                      : "outlined"
                  }
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ marginTop: 1 }}>
              {config.daysOfWeek.length === 0
                ? "No days selected - filter applies to all days"
                : `Filter applies on: ${config.daysOfWeek
                    .map((d) => DAYS_OF_WEEK[d].short)
                    .join(", ")}`}
            </Typography>
          </FormControl>

          {/* Timezone Selection */}
          <FormControl fullWidth sx={{ marginBottom: 2 }}>
            <FormLabel>Timezone</FormLabel>
            <Autocomplete
              options={TIMEZONES}
              value={selectedTimezone || null}
              onChange={(_, value) => handleTimezoneChange(value)}
              getOptionLabel={(option) => option.label}
              groupBy={(option) => option.region}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for a timezone..."
                  size="small"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      UTC {option.offset}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            {selectedTimezone && (
              <Typography variant="caption" sx={{ marginTop: 1 }}>
                Selected: {selectedTimezone.label} (UTC {selectedTimezone.offset})
              </Typography>
            )}
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
